import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login/Login';
import AdminDashboard from './components/Dashboard/AdminDashboard';
import MerchantDashboard from './components/Dashboard/MerchantDashboard';
import ProtectedRoute from './components/ProtectedRoute';
import CompleteRegistration from './components/CompleteRegistration/CompleteRegistration';
import Register from './components/Register/Register';
import { UserType } from './types';
import Dashboard from './components/Admin/Dashboard/Dashboard';
import Users from './components/Admin/Users/Users';
import PendingUsers from './components/Admin/PendingUsers/PendingUsers';
import Profile from './components/Admin/Profile/Profile';
import UserDetails from './components/Admin/UserDetails/UserDetails';
import Settings from './components/Admin/Settings/Settings';
import Webhooks from './components/Admin/Webhooks/Webhooks';
import Companies from './components/Admin/Companies/Companies';
import CompanyDetails from './components/Admin/CompanyDetails/CompanyDetails';
import Currencies from './components/Admin/Currencies/Currencies';
import CurrencyDetails from './components/Admin/CurrencyDetails/CurrencyDetails';
import BlockchainNetworks from './components/Admin/BlockchainNetworks/BlockchainNetworks';
import BlockchainNetworkDetails from './components/Admin/BlockchainNetworkDetails/BlockchainNetworkDetails';
import ContractAddresses from './components/Admin/ContractAddresses/ContractAddresses';
import ContractAddressDetails from './components/Admin/ContractAddressDetails/ContractAddressDetails';
import RpcEndpoints from './components/Admin/RpcEndpoints/RpcEndpoints';
import RpcEndpointDetails from './components/Admin/RpcEndpointDetails/RpcEndpointDetails';
import EmailTemplates from './components/Admin/EmailTemplates/EmailTemplates';
import EmailLogs from './components/Admin/EmailLogs/EmailLogs';
import LoginHistory from './components/Admin/LoginHistory/LoginHistory';
import CriticalNotifications from './components/Admin/CriticalNotifications/CriticalNotifications';
import MerchantProfile from './components/Merchant/Profile/Profile';
import CompanyProfile from './components/Merchant/CompanyProfile/CompanyProfile';
import MerchantDashboardHome from './components/Merchant/Dashboard/Dashboard';
import Staff from './components/Merchant/Staff/Staff';
import PendingStaff from './components/Merchant/PendingStaff/PendingStaff';
import MerchantSettings from './components/Merchant/Settings/Settings';
import MerchantBlockchainNetworks from './components/Merchant/PublicData/BlockchainNetworks';
import MerchantCurrencies from './components/Merchant/PublicData/Currencies';
import MerchantContractAddresses from './components/Merchant/PublicData/ContractAddresses';
import ApiKeys from './components/Merchant/ApiKeys/ApiKeys';
import MerchantWebhooks from './components/Merchant/Webhooks/Webhooks';
import SupportedBlockchainNetworks from './components/Merchant/SupportedResources/SupportedBlockchainNetworks';
import SupportedCurrencies from './components/Merchant/SupportedResources/SupportedCurrencies';
import SupportedContractAddresses from './components/Merchant/SupportedResources/SupportedContractAddresses';
import Wallets from './components/Admin/Wallets/Wallets';
import WalletDetails from './components/Admin/Wallets/WalletDetails';
import SweepList from './components/Admin/SweepList/SweepList';
import SweepDetails from './components/Admin/SweepList/SweepDetails';
import MoralisIncomingTransactions from './components/Admin/MoralisIncoming/MoralisIncomingTransactions';
import Moralis from './components/Admin/Moralis/Moralis';
import MerchantWallets from './components/Merchant/Wallets/MerchantWallets';
import MerchantWalletDetails from './components/Merchant/Wallets/MerchantWalletDetails';
import MerchantSweepList from './components/Merchant/SweepList/MerchantSweepList';
import MerchantSweepDetails from './components/Merchant/SweepList/MerchantSweepDetails';
import MerchantCriticalNotifications from './components/Merchant/CriticalNotifications/MerchantCriticalNotifications';
import MerchantCustodialWalletRequests from './components/Merchant/CustodialWalletRequests/MerchantCustodialWalletRequests';
import MerchantUpdateRequests from './components/Admin/MerchantUpdateRequests/MerchantUpdateRequests';
import Maintenance from './components/Admin/Maintenance/Maintenance';
import MPCService from './components/Admin/MPCService/MPCService';
import BirthdayEmailsLog from './components/Admin/InternalEmails/BirthdayEmailsLog';
import InternalEmails from './components/Admin/InternalEmails/InternalEmails';
import CreditTokens from './components/Admin/CreditTokens/CreditTokens';
import SubscriptionPlans from './components/Admin/SubscriptionPlans/SubscriptionPlans';
import VatConfigurations from './components/Admin/VatConfigurations/VatConfigurations';
import Invoices from './components/Admin/Invoices/Invoices';
import StaffDetails from './components/Merchant/Staff/StaffDetails';
import MerchantLoginHistory from './components/Merchant/LoginHistory/LoginHistory';
import AdminGasStation from './components/Admin/GasStation/GasStation';
import AdminInternalWallet from './components/Admin/InternalWallet/InternalWallet';
import MerchantGasStation from './components/Merchant/GasStation/GasStation';
import MerchantInternalWallet from './components/Merchant/InternalWallet/InternalWallet';
import MerchantInternalEmails from './components/Merchant/InternalEmails/MerchantInternalEmails';
import Billing from './components/Merchant/Billing/Billing';
import MerchantKyc from './components/Merchant/Kyc/Kyc';
import BillingReports from './components/Admin/BillingReports/BillingReports';
import { GlobalStyles } from './GlobalStyles';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <GlobalStyles />
        <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/complete-register" element={<CompleteRegistration />} />
        <Route
          path="/kivu"
          element={
            <ProtectedRoute requiredUserType={UserType.Administrator}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="users/Administrators" element={<Users />} />
          <Route path="users/Merchants" element={<Users />} />
          <Route path="users/:userId" element={<UserDetails />} />
          <Route path="pending-users" element={<PendingUsers />} />
          <Route path="companies" element={<Companies />} />
          <Route path="companies/:companyId" element={<CompanyDetails />} />
          <Route path="companies/:companyId/gas-station" element={<AdminGasStation />} />
          <Route path="companies/:companyId/internal-wallet" element={<AdminInternalWallet />} />
          <Route path="currencies" element={<Currencies />} />
          <Route path="currencies/:currencyId" element={<CurrencyDetails />} />
          <Route path="blockchain-networks" element={<BlockchainNetworks />} />
          <Route path="blockchain-networks/:networkId" element={<BlockchainNetworkDetails />} />
          <Route path="contract-addresses" element={<ContractAddresses />} />
          <Route path="contract-addresses/:contractId" element={<ContractAddressDetails />} />
          <Route path="rpc-endpoints" element={<RpcEndpoints />} />
          <Route path="rpc-endpoints/:rpcId" element={<RpcEndpointDetails />} />
          <Route path="email-templates" element={<EmailTemplates />} />
          <Route path="email-logs" element={<EmailLogs />} />
          <Route path="login-history" element={<LoginHistory />} />
          <Route path="critical-notifications" element={<CriticalNotifications />} />
          <Route path="wallets" element={<Wallets />} />
          <Route path="wallets/:walletId" element={<WalletDetails />} />
          <Route path="sweep-list" element={<SweepList />} />
          <Route path="sweep-list/:sweepId" element={<SweepDetails />} />
          <Route path="incoming-deposits" element={<MoralisIncomingTransactions />} />
          <Route path="moralis" element={<Moralis />} />
          <Route path="merchant-update-requests" element={<MerchantUpdateRequests />} />
          <Route path="mpc-service" element={<MPCService />} />
          <Route path="maintenance" element={<Maintenance />} />
          <Route path="settings" element={<Settings />} />
          <Route path="webhooks" element={<Webhooks />} />
          <Route path="profile" element={<Profile />} />
          <Route path="birthday-emails" element={<BirthdayEmailsLog />} />
          <Route path="internal-emails" element={<InternalEmails />} />
          <Route path="credit-tokens" element={<CreditTokens />} />
          <Route path="subscription-plans" element={<SubscriptionPlans />} />
          <Route path="vat-configurations" element={<VatConfigurations />} />
          <Route path="invoices" element={<Invoices />} />
          <Route path="billing-reports" element={<BillingReports />} />
        </Route>
        <Route
          path="/igwena"
          element={
            <ProtectedRoute requiredUserType={UserType.Merchant}>
              <MerchantDashboard />
            </ProtectedRoute>
          }
        >
          <Route index element={<MerchantDashboardHome />} />
          <Route path="profile" element={<MerchantProfile />} />
          <Route path="company" element={<CompanyProfile />} />
          <Route path="staff" element={<Staff />} />
          <Route path="staff/:staffId" element={<StaffDetails />} />
          <Route path="staff/pending" element={<PendingStaff />} />
          <Route path="login-history" element={<MerchantLoginHistory />} />
          <Route path="settings" element={<MerchantSettings />} />
          <Route path="blockchain-networks" element={<MerchantBlockchainNetworks />} />
          <Route path="currencies" element={<MerchantCurrencies />} />
          <Route path="contract-addresses" element={<MerchantContractAddresses />} />
          <Route path="api-keys" element={<ApiKeys />} />
          <Route path="webhooks" element={<MerchantWebhooks />} />
          <Route path="supported-blockchain-networks" element={<SupportedBlockchainNetworks />} />
          <Route path="supported-currencies" element={<SupportedCurrencies />} />
          <Route path="supported-contract-addresses" element={<SupportedContractAddresses />} />
          <Route path="wallets" element={<MerchantWallets />} />
          <Route path="wallets/:walletId" element={<MerchantWalletDetails />} />
          <Route path="sweep-list" element={<MerchantSweepList />} />
          <Route path="sweep-list/:sweepId" element={<MerchantSweepDetails />} />
          <Route path="gas-station" element={<MerchantGasStation />} />
          <Route path="internal-wallet" element={<MerchantInternalWallet />} />
          <Route
            path="critical-notifications/account-update-requests"
            element={<MerchantCustodialWalletRequests />}
          />
          <Route path="critical-notifications" element={<MerchantCriticalNotifications />} />
          <Route path="internal-emails" element={<MerchantInternalEmails />} />
          <Route path="kyc" element={<MerchantKyc />} />
          <Route path="billing" element={<Billing />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </ErrorBoundary>
  );
}

export default App;

